import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, CheckCircle, AlertTriangle, XCircle, ArrowRight,
  ChevronDown, Calculator, Shield, Zap, Users, BarChart3, ChevronRight, Star
} from 'lucide-react';
import { supabase } from '../lib/supabase';

type Step = 'landing' | 'form' | 'simulator' | 'result';

interface LeadData {
  prenom: string;
  nom: string;
  email: string;
  fonction: string;
  organisation: string;
}

interface SimulatorData {
  tresorerie_actuelle: number;
  charges_mensuelles: number;
  subventions: number;
  date_subvention: string;
  recettes_mensuelles: number;
  depenses_exceptionnelles: number;
}

interface SimulationResult {
  tresorerie_dans_6_mois: number;
  mois_couverture: number;
  date_tension: Date | null;
  niveau: 'vert' | 'orange' | 'rouge';
}

function calcResult(d: SimulatorData): SimulationResult {
  const tresorerie_dans_6_mois =
    d.tresorerie_actuelle +
    d.subventions +
    d.recettes_mensuelles * 6 -
    d.charges_mensuelles * 6 -
    d.depenses_exceptionnelles;

  const flux_net_mensuel = d.charges_mensuelles - d.recettes_mensuelles;
  let mois_couverture: number;
  if (flux_net_mensuel <= 0) {
    mois_couverture = 36;
  } else {
    const base = d.tresorerie_actuelle + d.subventions - d.depenses_exceptionnelles;
    mois_couverture = Math.max(0, base / flux_net_mensuel);
  }

  let date_tension: Date | null = null;
  if (mois_couverture < 36) {
    date_tension = new Date();
    date_tension.setMonth(date_tension.getMonth() + Math.floor(mois_couverture));
  }

  let niveau: 'vert' | 'orange' | 'rouge';
  if (mois_couverture >= 6) niveau = 'vert';
  else if (mois_couverture >= 2) niveau = 'orange';
  else niveau = 'rouge';

  return { tresorerie_dans_6_mois, mois_couverture, date_tension, niveau };
}

const fmtEur = (n: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

const fmtDate = (d: Date) =>
  new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' }).format(d);

function getUTM(key: string) {
  return new URLSearchParams(window.location.search).get(key) || '';
}

function fireEvent(name: string, data?: Record<string, unknown>) {
  if ((window as any).gtag) (window as any).gtag('event', name, data || {});
  if ((window as any).fbq) (window as any).fbq('track', name, data || {});
}

const RISK = {
  vert: {
    bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800',
    badgeBg: 'bg-green-100', icon: <CheckCircle className="text-green-600 flex-shrink-0" size={28} />,
    label: 'Situation stable', barColor: 'bg-green-500',
    message: "Votre association dispose d'une trésorerie solide sur les 6 prochains mois. Continuez à surveiller vos flux pour maintenir cette stabilité.",
  },
  orange: {
    bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800',
    badgeBg: 'bg-orange-100', icon: <AlertTriangle className="text-orange-500 flex-shrink-0" size={28} />,
    label: 'Tension possible', barColor: 'bg-orange-500',
    message: 'Votre trésorerie peut se retrouver sous tension dans les prochains mois. Une planification rigoureuse de vos flux est fortement recommandée.',
  },
  rouge: {
    bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800',
    badgeBg: 'bg-red-100', icon: <XCircle className="text-red-600 flex-shrink-0" size={28} />,
    label: 'Risque de rupture', barColor: 'bg-red-500',
    message: "Votre association est en situation de risque élevé de rupture de trésorerie. Une action immédiate est nécessaire pour anticiper et sécuriser vos financements.",
  },
};

const FAQ_ITEMS = [
  {
    q: 'Ce simulateur est-il gratuit ?',
    a: 'Oui, entièrement gratuit. Aucune carte bancaire ni abonnement requis.',
  },
  {
    q: 'Ce simulateur remplace-t-il un logiciel de gestion ?',
    a: "Non. Il permet d'obtenir une estimation rapide de votre situation de trésorerie. Pour un suivi complet au quotidien, TrezoOne propose des fonctionnalités avancées adaptées aux associations.",
  },
  {
    q: 'Les données saisies sont-elles conservées ?',
    a: 'Vos informations sont utilisées uniquement pour générer votre simulation et vous transmettre des ressources utiles. Elles ne sont jamais partagées avec des tiers.',
  },
  {
    q: 'Quelles associations peuvent utiliser ce simulateur ?',
    a: "Toutes les associations loi 1901, fondations, comités d'entreprise ou structures assimilées, quelle que soit leur taille ou leur secteur d'activité.",
  },
];

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-gray-900 pr-4">{q}</span>
        <ChevronDown
          size={18}
          className={`text-gray-400 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-6 pb-5 pt-1 bg-white border-t border-gray-100">
          <p className="text-gray-600 leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

export function SimulateurTresorerieAssociationPage() {
  const navigate = useNavigate();
  const simulatorRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState<Step>('landing');

  const [lead, setLead] = useState<LeadData>({ prenom: '', nom: '', email: '', fonction: '', organisation: '' });
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadError, setLeadError] = useState('');
  const [prospectId, setProspectId] = useState<string | null>(null);

  const [sim, setSim] = useState<SimulatorData>({
    tresorerie_actuelle: 0, charges_mensuelles: 0, subventions: 0,
    date_subvention: '', recettes_mensuelles: 0, depenses_exceptionnelles: 0,
  });
  const [result, setResult] = useState<SimulationResult | null>(null);

  useEffect(() => {
    fireEvent('simulateur_started');
    document.title = 'Simulateur de trésorerie association gratuit | TrezoOne';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Testez gratuitement la trésorerie de votre association en 30 secondes grâce au simulateur TrezoOne.');

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    });
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  function scrollToForm() {
    setStep('form');
    setTimeout(() => simulatorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  async function handleLeadSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!lead.prenom || !lead.nom || !lead.email || !lead.fonction || !lead.organisation) {
      setLeadError('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setLeadLoading(true);
    setLeadError('');
    try {
      const { data, error } = await supabase
        .from('crm_prospects')
        .insert({
          organization_name: lead.organisation,
          prenom: lead.prenom,
          nom: lead.nom,
          contact_email: lead.email,
          contact_name: `${lead.prenom} ${lead.nom}`,
          fonction: lead.fonction,
          source: 'simulateur_tresorerie',
          tag: 'simulateur_landing',
          utm_source: getUTM('utm_source'),
          utm_campaign: getUTM('utm_campaign'),
          status: 'insomnie_detectee',
          estimated_cash: 0,
          estimated_monthly_expenses: 0,
          imf_score: 3,
          declic_reached: false,
          estimated_mrr: 0,
        })
        .select()
        .maybeSingle();

      if (error) throw error;
      if (data) setProspectId(data.id);
      setStep('simulator');
      setTimeout(() => simulatorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } catch {
      setLeadError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLeadLoading(false);
    }
  }

  async function handleSimSubmit(e: React.FormEvent) {
    e.preventDefault();
    const res = calcResult(sim);
    setResult(res);

    if (prospectId) {
      await supabase
        .from('crm_prospects')
        .update({
          estimated_cash: sim.tresorerie_actuelle,
          estimated_monthly_expenses: sim.charges_mensuelles,
          simulation_data: { ...sim },
        })
        .eq('id', prospectId);
    }

    fireEvent('simulateur_completed', { niveau_risque: res.niveau, mois: Math.round(res.mois_couverture) });
    if ((window as any).fbq) (window as any).fbq('track', 'Lead');
    setStep('result');
    setTimeout(() => simulatorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  function handleSimChange(field: keyof SimulatorData, val: string) {
    setSim(prev => ({ ...prev, [field]: field === 'date_subvention' ? val : parseFloat(val) || 0 }));
  }

  const riskCfg = result ? RISK[result.niveau] : null;

  return (
    <div className="min-h-screen bg-white">
      {/* HEADER */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="text-white" size={20} />
              </div>
              <span className="text-lg font-bold text-gray-900">TrezoOne</span>
            </button>
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/login')} className="hidden sm:block text-gray-600 hover:text-blue-600 font-medium text-sm transition-colors">
                Se connecter
              </button>
              <button
                onClick={() => navigate('/login')}
                className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
              >
                Essai gratuit
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* HERO — conversion-first */}
      <section className="bg-gradient-to-b from-slate-50 to-white pt-14 pb-16 px-4">
        <div className="max-w-2xl mx-auto text-center">

          {/* Badge */}
          <div className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-100 text-blue-700 px-3.5 py-1.5 rounded-full text-xs font-semibold mb-8 tracking-wide">
            <Calculator size={12} />
            Outil gratuit pour associations loi 1901
          </div>

          {/* Headline */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-5 tracking-tight">
            Votre association tiendra-t-elle jusqu'à la prochaine{' '}
            <span className="text-blue-600">subvention&nbsp;?</span>
          </h1>

          {/* Accroche */}
          <p className="text-base sm:text-lg font-semibold text-gray-500 mb-3">
            Le vrai risque des associations n'est pas le budget. C'est la trésorerie.
          </p>
          <p className="text-sm sm:text-base text-gray-500 mb-10 max-w-lg mx-auto leading-relaxed">
            Découvrez en moins de 30 secondes si votre association risque une rupture de trésorerie avant la prochaine subvention.
          </p>

          {/* CTA principal */}
          <button
            onClick={scrollToForm}
            className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-xl hover:bg-blue-700 active:scale-95 transition-all shadow-lg hover:shadow-xl text-base sm:text-lg font-bold w-full sm:w-auto"
          >
            Tester mon risque de trésorerie
            <ArrowRight size={20} />
          </button>

          {/* Micro-réassurance */}
          <p className="mt-3 text-xs text-gray-400 font-medium tracking-wide">
            Sans inscription &mdash; résultat immédiat
          </p>

          {/* Preuve sociale */}
          <div className="mt-8 flex items-center justify-center gap-1.5">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={13} className="text-yellow-400 fill-yellow-400" />
            ))}
            <span className="text-sm text-gray-400 ml-1.5">Déjà utilisé par plus de 200 associations</span>
          </div>
        </div>
      </section>

      {/* BÉNÉFICES */}
      <section className="py-10 px-4 border-y border-gray-100 bg-white">
        <div className="max-w-2xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { icon: <Zap size={20} className="text-blue-600" />, title: 'Résultat immédiat', desc: 'Votre diagnostic en moins de 30 secondes, sans attente.' },
              { icon: <Users size={20} className="text-blue-600" />, title: 'Conçu pour les associations', desc: 'Pensé pour les réalités des associations loi 1901.' },
              { icon: <Shield size={20} className="text-blue-600" />, title: 'Aucun logiciel à installer', desc: 'Fonctionne directement dans votre navigateur.' },
            ].map((b, i) => (
              <div key={i} className="flex flex-col items-center text-center gap-2 px-4 py-5 rounded-xl bg-gray-50 border border-gray-100">
                <div className="w-10 h-10 bg-white border border-gray-100 rounded-xl flex items-center justify-center shadow-sm">
                  {b.icon}
                </div>
                <span className="font-semibold text-gray-900 text-sm">{b.title}</span>
                <span className="text-xs text-gray-500 leading-relaxed">{b.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ANCHOR — form / simulator / result */}
      <div ref={simulatorRef} className="scroll-mt-20" />

      {/* FORMULAIRE */}
      {(step === 'landing' || step === 'form') && (
        <section className="py-16 px-4 bg-white">
          <div className="max-w-lg mx-auto">
            <div className="text-center mb-8">
              <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Calculator className="text-blue-600" size={22} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1.5">Accédez au simulateur gratuit</h2>
              <p className="text-gray-500 text-sm">Renseignez vos coordonnées pour accéder à votre simulation personnalisée</p>
            </div>

            <form onSubmit={handleLeadSubmit} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-7 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Prénom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={lead.prenom}
                    onChange={e => setLead(p => ({ ...p, prenom: e.target.value }))}
                    placeholder="Marie"
                    required
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={lead.nom}
                    onChange={e => setLead(p => ({ ...p, nom: e.target.value }))}
                    placeholder="Dupont"
                    required
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email professionnel <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={lead.email}
                  onChange={e => setLead(p => ({ ...p, email: e.target.value }))}
                  placeholder="contact@votre-association.fr"
                  required
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Votre fonction <span className="text-red-500">*</span>
                </label>
                <select
                  value={lead.fonction}
                  onChange={e => setLead(p => ({ ...p, fonction: e.target.value }))}
                  required
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                >
                  <option value="">Sélectionner votre fonction</option>
                  <option value="Président(e)">Président(e)</option>
                  <option value="Trésorier(e)">Trésorier(e)</option>
                  <option value="Directeur(rice)">Directeur(rice)</option>
                  <option value="Responsable administratif">Responsable administratif</option>
                  <option value="Comptable">Comptable</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Nom de l'association <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={lead.organisation}
                  onChange={e => setLead(p => ({ ...p, organisation: e.target.value }))}
                  placeholder="Association culturelle de..."
                  required
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              {leadError && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700 text-sm">
                  {leadError}
                </div>
              )}

              <button
                type="submit"
                disabled={leadLoading}
                className="w-full bg-blue-600 text-white py-3.5 rounded-xl hover:bg-blue-700 active:scale-95 transition-all font-bold text-base flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
              >
                {leadLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>Accéder au simulateur gratuit <ChevronRight size={18} /></>
                )}
              </button>

              <p className="text-xs text-gray-400 text-center leading-relaxed">
                Ces informations nous permettent de vous transmettre votre simulation et des ressources utiles pour piloter la trésorerie de votre association.
              </p>
            </form>
          </div>
        </section>
      )}

      {/* SIMULATEUR */}
      {step === 'simulator' && (
        <section className="py-16 px-4 bg-white">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
                <CheckCircle size={14} />
                Accès débloqué — {lead.prenom || 'bienvenue'}
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Simulez la trésorerie de votre association</h2>
              <p className="text-gray-500 text-sm">Renseignez vos données financières pour obtenir votre diagnostic</p>
            </div>

            <form onSubmit={handleSimSubmit} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 space-y-8">
              <div className="space-y-5">
                <div className="flex items-center gap-3 pb-2 border-b border-gray-100">
                  <div className="w-7 h-7 bg-blue-600 text-white rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
                  <h3 className="font-bold text-gray-900">Situation actuelle</h3>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Trésorerie actuelle (€) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number" min="0" step="100" required
                    value={sim.tresorerie_actuelle || ''}
                    onChange={e => handleSimChange('tresorerie_actuelle', e.target.value)}
                    placeholder="Ex : 25 000"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-400 mt-1">Solde total de vos comptes bancaires aujourd'hui</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Charges mensuelles (€) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number" min="0" step="100" required
                      value={sim.charges_mensuelles || ''}
                      onChange={e => handleSimChange('charges_mensuelles', e.target.value)}
                      placeholder="Ex : 8 000"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Recettes mensuelles (€)
                    </label>
                    <input
                      type="number" min="0" step="100"
                      value={sim.recettes_mensuelles || ''}
                      onChange={e => handleSimChange('recettes_mensuelles', e.target.value)}
                      placeholder="Ex : 3 000"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-400 mt-1">Hors subventions</p>
                  </div>
                </div>
              </div>

              <div className="space-y-5">
                <div className="flex items-center gap-3 pb-2 border-b border-gray-100">
                  <div className="w-7 h-7 bg-blue-600 text-white rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
                  <h3 className="font-bold text-gray-900">Subventions et dépenses à venir</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Subventions à recevoir (€)</label>
                    <input
                      type="number" min="0" step="100"
                      value={sim.subventions || ''}
                      onChange={e => handleSimChange('subventions', e.target.value)}
                      placeholder="Ex : 15 000"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Date de réception estimée</label>
                    <input
                      type="date"
                      value={sim.date_subvention}
                      onChange={e => handleSimChange('date_subvention', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Dépenses exceptionnelles prévues (€)</label>
                  <input
                    type="number" min="0" step="100"
                    value={sim.depenses_exceptionnelles || ''}
                    onChange={e => handleSimChange('depenses_exceptionnelles', e.target.value)}
                    placeholder="Ex : 5 000 (achat matériel, événement...)"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-4 rounded-xl hover:bg-blue-700 active:scale-95 transition-all font-bold text-base flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
              >
                Obtenir mon diagnostic de trésorerie
                <ArrowRight size={20} />
              </button>
            </form>
          </div>
        </section>
      )}

      {/* RÉSULTAT */}
      {step === 'result' && result && riskCfg && (
        <section className="py-16 px-4 bg-white">
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Votre diagnostic de trésorerie</h2>
              <p className="text-gray-500 text-sm">Basé sur les données de {lead.organisation || 'votre association'}</p>
            </div>

            <div className={`${riskCfg.bg} ${riskCfg.border} border-2 rounded-2xl p-6`}>
              <div className="flex items-start gap-4">
                {riskCfg.icon}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`${riskCfg.badgeBg} ${riskCfg.text} px-3 py-1 rounded-full text-sm font-bold`}>
                      {riskCfg.label}
                    </span>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">{riskCfg.message}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white border border-gray-200 rounded-xl p-5 text-center shadow-sm">
                <div className="text-4xl font-black text-gray-900 mb-1">
                  {result.mois_couverture >= 36 ? '36+' : Math.round(result.mois_couverture)}
                </div>
                <div className="text-xs text-gray-500 font-medium leading-tight">Mois de<br />trésorerie</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-5 text-center shadow-sm">
                <div className="text-base font-bold text-gray-900 mb-1 leading-tight">
                  {result.date_tension ? fmtDate(result.date_tension) : 'Pas de risque identifié'}
                </div>
                <div className="text-xs text-gray-500 font-medium">Date de tension estimée</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-5 text-center shadow-sm">
                <div className={`text-base font-bold mb-1 ${result.tresorerie_dans_6_mois >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {fmtEur(result.tresorerie_dans_6_mois)}
                </div>
                <div className="text-xs text-gray-500 font-medium">Trésorerie dans 6 mois</div>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
              <div className="text-sm font-semibold text-gray-700 mb-3">Votre simulation en détail</div>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Trésorerie actuelle</span>
                  <span className="font-medium text-gray-900">{fmtEur(sim.tresorerie_actuelle)}</span>
                </div>
                <div className="flex justify-between">
                  <span>+ Subventions à recevoir</span>
                  <span className="font-medium text-green-700">+{fmtEur(sim.subventions)}</span>
                </div>
                <div className="flex justify-between">
                  <span>+ Recettes sur 6 mois</span>
                  <span className="font-medium text-green-700">+{fmtEur(sim.recettes_mensuelles * 6)}</span>
                </div>
                <div className="flex justify-between">
                  <span>- Charges sur 6 mois</span>
                  <span className="font-medium text-red-600">-{fmtEur(sim.charges_mensuelles * 6)}</span>
                </div>
                <div className="flex justify-between">
                  <span>- Dépenses exceptionnelles</span>
                  <span className="font-medium text-red-600">-{fmtEur(sim.depenses_exceptionnelles)}</span>
                </div>
                <div className="border-t border-gray-200 pt-2 flex justify-between font-bold text-gray-900">
                  <span>Projection à 6 mois</span>
                  <span className={result.tresorerie_dans_6_mois >= 0 ? 'text-green-700' : 'text-red-600'}>
                    {fmtEur(result.tresorerie_dans_6_mois)}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-blue-600 rounded-2xl p-8 text-white text-center">
              <h3 className="text-xl font-bold mb-2">Aller plus loin avec TrezoOne</h3>
              <p className="text-blue-100 mb-6 leading-relaxed text-sm max-w-md mx-auto">
                Pilotez votre trésorerie en temps réel, anticipez les tensions et suivez vos subventions avec un outil conçu pour les associations.
              </p>
              <div className="grid grid-cols-2 gap-2 mb-6 text-left max-w-sm mx-auto">
                {['Prévisions sur 12 mois', 'Suivi des subventions', 'Alertes de tension', 'Rapports pour financeurs'].map(f => (
                  <div key={f} className="flex items-center gap-1.5 text-sm text-blue-100">
                    <CheckCircle size={13} className="text-green-400 flex-shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate('/login')}
                className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-xl hover:bg-blue-50 transition-all font-bold text-base shadow-lg"
              >
                Essayer TrezoOne gratuitement
                <ArrowRight size={18} />
              </button>
              <p className="text-blue-200 text-xs mt-3">14 jours d'essai · Sans carte bancaire</p>
            </div>

            <button
              onClick={() => { setStep('simulator'); setTimeout(() => simulatorRef.current?.scrollIntoView({ behavior: 'smooth' }), 50); }}
              className="w-full text-center text-gray-400 hover:text-gray-600 text-sm py-2 transition-colors"
            >
              Modifier ma simulation
            </button>
          </div>
        </section>
      )}

      {/* SECTION TREZOONE */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-8 bg-blue-600 rounded-full" />
            <span className="text-sm font-semibold text-blue-600 uppercase tracking-wide">La solution complète</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Aller plus loin avec TrezoOne</h2>
          <p className="text-gray-600 mb-9 max-w-xl text-sm leading-relaxed">
            TrezoOne est un outil conçu pour aider les associations à piloter leur trésorerie simplement, sans compétences comptables avancées.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-9">
            {[
              { icon: <BarChart3 size={18} className="text-blue-600" />, title: 'Prévisions 12 mois', desc: 'Visualisez vos flux futurs et anticipez les tensions avant qu\'elles surviennent.' },
              { icon: <CheckCircle size={18} className="text-green-600" />, title: 'Suivi des subventions', desc: 'Suivez l\'avancement de chaque dossier et son impact sur vos comptes.' },
              { icon: <AlertTriangle size={18} className="text-orange-500" />, title: 'Alertes automatiques', desc: 'Recevez une alerte dès que votre trésorerie approche d\'un seuil critique.' },
              { icon: <TrendingUp size={18} className="text-blue-600" />, title: 'Tableaux de bord', desc: 'Tous vos flux financiers en un coup d\'œil, accessible à tout moment.' },
            ].map(item => (
              <div key={item.title} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center mb-3">
                  {item.icon}
                </div>
                <div className="font-semibold text-gray-900 text-sm mb-1">{item.title}</div>
                <div className="text-xs text-gray-500 leading-relaxed">{item.desc}</div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <button
              onClick={() => navigate('/fonctionnalites')}
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3.5 rounded-xl hover:bg-blue-700 transition-all font-semibold shadow-md text-sm"
            >
              Découvrir TrezoOne
              <ArrowRight size={17} />
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Questions fréquentes</h2>
          <div className="space-y-3">
            {FAQ_ITEMS.map(item => <FAQItem key={item.q} q={item.q} a={item.a} />)}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-16 px-4 bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="max-w-xl mx-auto text-center text-white">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">Pilotez votre trésorerie sereinement</h2>
          <p className="text-blue-100 text-sm sm:text-base mb-8 leading-relaxed">
            Anticiper les décalages de trésorerie permet d'éviter de nombreuses difficultés financières. TrezoOne aide les associations à suivre leur trésorerie en temps réel.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-xl hover:bg-blue-50 transition-all font-bold text-base shadow-xl"
          >
            Essayer TrezoOne
            <ArrowRight size={19} />
          </button>
          <p className="text-blue-200 text-xs mt-4">14 jours d'essai gratuit · Sans engagement · Sans carte bancaire</p>
        </div>
      </section>

      <footer className="bg-gray-900 text-gray-400 py-10 px-4">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
              <TrendingUp className="text-white" size={14} />
            </div>
            <span className="font-semibold text-white">TrezoOne</span>
          </div>
          <p className="text-sm text-gray-500 text-center">
            © {new Date().getFullYear()} TrezoOne — Gestion de trésorerie pour les associations
          </p>
          <div className="flex gap-4 text-sm">
            <button onClick={() => navigate('/tarifs')} className="hover:text-white transition-colors">Tarifs</button>
            <button onClick={() => navigate('/fonctionnalites')} className="hover:text-white transition-colors">Fonctionnalités</button>
            <button onClick={() => navigate('/login')} className="hover:text-white transition-colors">Connexion</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
